import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Clock, Download, FileCheck, FileText, Files, Gift, Search } from 'lucide-react';
import { Breadcrumbs, Button, Card, Container, EmptyState, ErrorNote, InfoRow, Skeleton } from '../components/ui';
import AccessModal from '../components/access/AccessModal';
import AuthContext from '../context/AuthContext';
import useAsync from '../hooks/useAsync';
import useEntitlements from '../hooks/useEntitlements';
import useSeo from '../hooks/useSeo';
import { catalog, documents, downloads as downloadsApi, errorCode, errorMessage } from '../lib/api';
import { catalogCrumbs, catalogPath, documentSeo } from '../lib/catalogSeo';
import { correctionOf, documentTitle, formatDate, formatFileSize, formatRenewal, hasCorrection, hasIncludedCorrection, labelOf, nodeChain, organismeLabel } from '../lib/format';
import { NOINDEX } from '../lib/seo';
import { trackEvent } from '../lib/analytics';
import { segmentFor, slugOf } from '../lib/slug';

const ACCESS_CODES = ['AUTH_REQUIRED', 'SUBSCRIPTION_REQUIRED', 'DAILY_LIMIT_REACHED', 'ACCOUNT_DISABLED'];

/**
 * Téléchargement contrôlé : le bouton est un guide, le serveur reste seul juge.
 * Sujets de 2010 et avant : gratuits, sans compte. Sujets récents : quota glissant (visiteur,
 * compte ou abonné). En cas de refus, la fenêtre d'accès explique la situation et la suite.
 */
const useProtectedDownload = (next) => {
  const { user } = useContext(AuthContext);
  const entitlements = useEntitlements();
  const quota = useAsync(() => downloadsApi.quota(), [user?._id || user?.id || 'visiteur']);
  const [reason, setReason] = useState(null);
  const [limitInfo, setLimitInfo] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  const download = async (documentId) => {
    setError(null);
    trackEvent('download_click', { document_id: documentId });
    setBusyId(documentId);
    // Onglet ouvert pendant le clic : un window.open après une requête serait bloqué.
    const tab = window.open('about:blank', '_blank');
    try {
      const { url } = await documents.downloadUrl(documentId);
      if (tab) {
        tab.opener = null;
        tab.location.href = url;
      } else {
        window.location.assign(url);
      }
      quota.reload();
      if (user) entitlements.reload();
    } catch (requestError) {
      tab?.close();
      const code = errorCode(requestError);
      if (ACCESS_CODES.includes(code)) {
        setLimitInfo(requestError.response?.data?.details || null);
        setReason(code);
        quota.reload();
      } else {
        setError(errorMessage(requestError, 'Le téléchargement n’a pas pu démarrer.'));
      }
    } finally {
      setBusyId(null);
    }
  };

  return {
    download,
    busyId,
    error,
    quota: quota.data,
    entitlements: entitlements.data,
    modal: <AccessModal reason={reason} onClose={() => setReason(null)} next={next} entitlements={entitlements.data} limitInfo={limitInfo} />,
  };
};

/** État d'accès d'un document pour l'affichage : gratuit, récent disponible, ou quota épuisé. */
const accessState = (document, quota) => {
  const access = document?.access;
  if (access?.tier === 'free') return { kind: 'free', access };
  if (quota?.unlimited) return { kind: 'recent', access, remaining: null };
  const alreadyGranted = quota?.documentIds?.includes?.(document?._id);
  if (quota && quota.remaining === 0 && !alreadyGranted) return { kind: 'exhausted', access, quota };
  return { kind: 'recent', access, remaining: quota?.remaining, quota };
};

/**
 * Position du sujet dans le catalogue (organisme → parcours → niveaux → matière), avec l'adresse
 * de chaque étape : fil d'Ariane cliquable, et liens explorables par les moteurs de recherche.
 */
const catalogContext = (document, organismeList) => {
  const nodes = nodeChain(document?.noeudId);
  const organismeId = String(document?.noeudId?.organismeId || document?.matiereId?.organismeId || '');
  const organisme = organismeList.find((item) => String(item._id) === organismeId) || null;
  if (!organisme) return { organisme: null, nodes, crumbs: [] };
  const parcoursType = document.parcoursTypeId?.nom ? document.parcoursTypeId : null;
  const matiere = document.matiereId?.nom ? document.matiereId : null;
  const steps = [
    { label: organismeLabel(organisme), segment: segmentFor(organisme, organismeList) },
    parcoursType && { label: labelOf(parcoursType), segment: slugOf(parcoursType) },
    ...nodes.map((node) => ({ label: labelOf(node), segment: slugOf(node) })),
    matiere && { label: labelOf(matiere), segment: slugOf(matiere) },
  ].filter(Boolean);
  const crumbs = catalogCrumbs(steps.map(({ label }, index) => ({
    label,
    path: catalogPath(steps.slice(0, index + 1).map(({ segment }) => segment)),
  })));
  return { organisme, parcoursType, nodes, matiere, crumbs };
};

/** Téléchargement groupé, pré-filtré sur l'organisme, le cycle et l'année du sujet. */
const bulkPath = (context, document) => {
  const params = new URLSearchParams({ organisme: slugOf(context.organisme) || context.organisme._id });
  if (context.parcoursType?._id) params.set('cycle', context.parcoursType._id);
  if (document?.access?.year) params.set('annee', String(document.access.year));
  return `/telecharger?${params}`;
};

const DocumentPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const { data: document, loading, error, reload } = useAsync(() => documents.get(id), [id]);
  const organismes = useAsync(() => catalog.organismes({ limit: 100 }), []);
  const access = useProtectedDownload(location.pathname);
  const context = catalogContext(document, organismes.data?.data || []);

  const documentId = document?._id;
  const organismeName = context.organisme ? organismeLabel(context.organisme) : null;
  useEffect(() => {
    if (documentId && organismeName) trackEvent('view_item', { document_id: documentId, organisme: organismeName });
  }, [documentId, organismeName]);

  useSeo(document && !organismes.loading
    ? documentSeo({ ...document, hasCorrection: hasCorrection(document) }, context)
    : error ? { title: 'Sujet introuvable', description: 'Ce document n’existe pas ou n’est plus disponible.', robots: NOINDEX } : null);

  if (loading && !document) {
    return (
      <Container className="py-8 md:py-12">
        <div role="status" aria-label="Chargement du sujet" className="grid gap-8 lg:grid-cols-[1fr_360px] lg:gap-12">
          <div className="space-y-4">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="mt-6 h-4 w-24" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-3/4" />
            <Skeleton className="h-24 w-full" />
          </div>
          <Skeleton className="h-72 rounded-3xl" />
        </div>
      </Container>
    );
  }

  if (error || !document) {
    return (
      <Container className="max-w-3xl py-12">
        <EmptyState
          icon={FileText}
          title="Sujet introuvable"
          description="Ce document n’existe pas ou n’est plus disponible. Il a peut-être été déplacé : retrouvez-le par organisme, année ou matière."
          onRetry={error?.response ? undefined : reload}
          action={(
            <div className="flex flex-wrap justify-center gap-2">
              <Button to="/recherche" variant="secondary" size="sm" icon={Search}>Rechercher un sujet</Button>
              <Button to="/sujets" variant="ghost" size="sm">Parcourir les sujets</Button>
            </div>
          )}
        />
      </Container>
    );
  }

  const state = accessState(document, access.quota);
  const correction = correctionOf(document);
  const correctionIncluded = hasIncludedCorrection(document);
  const isCorrection = document.documentType === 'corrige' || document.type === 'correction';
  const chain = nodeChain(document.noeudId);
  const details = [
    ['Parcours', labelOf(document.parcoursTypeId)],
    ...chain.map((node) => ['Niveau', labelOf(node)]),
    ['Matière', labelOf(document.matiereId)],
    ['Institution', document.institution?.name],
    ['Université', document.university?.name],
    ['Session', document.semester?.name],
    ['Catégorie', document.category?.name],
    ['Format', [String(document.extension || 'pdf').replace('.', '').toUpperCase(), formatFileSize(document.fileSize)].filter(Boolean).join(' · ')],
    ['Ajouté le', formatDate(document.dateAjout || document.createdAt)],
  ].filter(([, value]) => Boolean(value));

  // Le fil d'Ariane donne déjà la position ; sans lui, la chaîne sert de repère au-dessus du titre.
  const chainLabel = [chain.map(labelOf).join(' · '), labelOf(document.matiereId)].filter(Boolean).join(' · ');
  const noun = isCorrection ? 'le corrigé' : 'le sujet';
  const format = String(document.extension || 'pdf').replace('.', '').toUpperCase();
  const listing = context.crumbs.length > 1 ? context.crumbs[context.crumbs.length - 1] : null;

  return (
    <Container className="py-6 md:py-12">
      {context.crumbs.length ? (
        <Breadcrumbs crumbs={context.crumbs.map((crumb) => ({ label: crumb.label, to: crumb.path }))} currentIsLink />
      ) : (
        <button type="button" onClick={() => window.history.back()} className="-ml-2 inline-flex h-10 items-center gap-1.5 rounded-lg px-2 text-sm font-medium text-ink-soft hover:text-ink">
          <ArrowLeft size={16} /> Retour
        </button>
      )}

      <div className="mt-5 grid gap-6 md:mt-8 lg:grid-cols-[1fr_360px] lg:gap-x-12 lg:gap-y-8">
        <header className="min-w-0 animate-rise-in lg:col-start-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-ink px-2.5 py-1 text-xs font-semibold text-white">
              <FileText size={12} aria-hidden /> {isCorrection ? 'Corrigé' : 'Sujet'}
            </span>
            {correction || correctionIncluded ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-gold-wash px-2.5 py-1 text-xs font-semibold text-gold-ink">
                <FileCheck size={12} aria-hidden /> {correction ? 'Corrigé disponible' : 'Corrigé inclus'}
              </span>
            ) : null}
            {!context.crumbs.length && chainLabel ? <span className="text-xs font-semibold uppercase tracking-[0.12em] text-gold-ink">{chainLabel}</span> : null}
          </div>
          <h1 className="mt-4 text-title-lg font-bold text-ink">{documentTitle(document)}</h1>
          {document.description ? <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-ink-soft">{document.description}</p> : null}
        </header>

        {/* Accès au fichier : en vue sur mobile juste après le titre, épinglé à droite sur ordinateur. */}
        <aside className="lg:col-start-2 lg:row-span-2 lg:row-start-1">
          <div className="animate-rise-in rounded-3xl border border-line bg-white p-5 shadow-soft [animation-delay:80ms] sm:p-6 lg:sticky lg:top-24">
            <div className="flex items-center gap-4">
              {/* Vignette du fichier : une feuille cornée, le format en étiquette. */}
              <div className="relative h-[72px] w-14 shrink-0 rounded-lg border border-line bg-paper shadow-soft" aria-hidden>
                <span className="absolute right-0 top-0 h-4 w-4 rounded-bl-md border-b border-l border-line bg-paper-dim" />
                <span className="absolute inset-x-2.5 top-6 h-1 rounded-full bg-ink/70" />
                <span className="absolute inset-x-2.5 top-[34px] h-1 rounded-full bg-paper-dim" />
                <span className="absolute left-2.5 right-5 top-[42px] h-1 rounded-full bg-paper-dim" />
                <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 rounded bg-burgundy px-1.5 py-0.5 text-[9px] font-bold tracking-wide text-white">{format}</span>
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-ink">
                  {state.kind === 'free' ? 'Sujet gratuit' : state.kind === 'exhausted' ? 'Quota de sujets récents atteint' : 'Prêt à télécharger'}
                </p>
                <p className="mt-0.5 text-sm text-ink-soft">
                  {[format, formatFileSize(document.fileSize)].filter(Boolean).join(' · ')}
                </p>
              </div>
            </div>

            {/* Règle d'accès du document : gratuit (ancien sujet) ou récent (quota). */}
            <p className={`mt-4 flex items-start gap-2 rounded-xl px-3 py-2.5 text-sm ${state.kind === 'free' ? 'bg-gold-wash text-gold-ink' : 'bg-paper-dim text-ink-soft'}`}>
              {state.kind === 'free' ? <Gift size={15} className="mt-0.5 shrink-0" aria-hidden /> : <Clock size={15} className="mt-0.5 shrink-0" aria-hidden />}
              <span>
                {state.kind === 'free'
                  ? `Sujet de ${state.access.year} : téléchargement gratuit et illimité, sans compte.`
                  : state.kind === 'exhausted'
                    ? `Vous avez utilisé vos téléchargements de sujets récents.${formatRenewal(state.quota.nextRenewalAt) ? ` Prochain téléchargement possible à partir du ${formatRenewal(state.quota.nextRenewalAt)}.` : ''} Les sujets de ${state.quota.freeUntilYear} et avant restent gratuits.`
                    : state.remaining === null || state.remaining === undefined
                      ? (state.access?.yearKnown === false ? 'Sujet récent (année non précisée).' : 'Sujet récent.')
                      : `Sujet récent : ${state.remaining} téléchargement${state.remaining > 1 ? 's' : ''} de sujet récent restant${state.remaining > 1 ? 's' : ''} sur ${state.quota.windowHours} heures${state.quota.plan === 'subscriber' ? '.' : ' (gratuit, sans abonnement).'}`}
              </span>
            </p>
            <div className="mt-5 grid gap-2">
              <Button size="lg" icon={Download} loading={access.busyId === document._id} onClick={() => access.download(document._id)}>
                {state.kind === 'free' ? `Télécharger ${noun} gratuitement` : `Télécharger ${noun}`}
              </Button>
              {correction ? (
                <Button size="lg" variant="secondary" icon={FileCheck} loading={access.busyId === correction._id} onClick={() => access.download(correction._id)}>
                  Télécharger le corrigé
                </Button>
              ) : null}
            </div>
            <ErrorNote className="mt-3">{access.error}</ErrorNote>

            {state.kind !== 'free' && access.quota && !access.quota.unlimited ? (
              <div className="mt-4">
                <div className="flex items-baseline justify-between text-xs text-ink-muted">
                  <span>{`Sujets récents (${access.quota.windowHours} h glissantes)`}</span>
                  <span className="tabular font-semibold text-ink-soft">{`${access.quota.used} / ${access.quota.limit}`}</span>
                </div>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-paper-dim" role="progressbar" aria-label="Sujets récents téléchargés sur la période" aria-valuenow={access.quota.used} aria-valuemin={0} aria-valuemax={access.quota.limit}>
                  <div className="h-full origin-left rounded-full bg-ink transition-transform duration-700 ease-emphasized" style={{ transform: `scaleX(${Math.min(1, access.quota.used / (access.quota.limit || 1))})` }} />
                </div>
                {access.quota.plan !== 'subscriber' ? (
                  <Link to={`/abonnement?next=${encodeURIComponent(location.pathname)}`} className="mt-2 inline-block text-xs font-semibold text-burgundy hover:underline">
                    {`Abonnés : jusqu’à ${access.quota.limits?.subscriber || 20} sujets récents par ${access.quota.windowHours} h`}
                  </Link>
                ) : null}
              </div>
            ) : null}

            {correctionIncluded && !correction ? (
              <p className="mt-4 flex items-start gap-2 border-t border-line pt-4 text-sm text-gold-ink">
                <FileCheck size={15} className="mt-0.5 shrink-0" aria-hidden /> Corrigé inclus : il se trouve dans le même PDF, à la suite du sujet.
              </p>
            ) : !isCorrection && !correction ? (
              <p className="mt-4 border-t border-line pt-4 text-sm text-ink-soft">Le corrigé de ce sujet n’a pas encore été publié.</p>
            ) : null}
          </div>
        </aside>

        <Card className="min-w-0 animate-rise-in self-start px-5 [animation-delay:120ms] lg:col-start-1">
          <h2 className="border-b border-line py-3.5 text-xs font-semibold uppercase tracking-[0.14em] text-ink-muted">Fiche du document</h2>
          {details.map(([label, value], index) => <InfoRow key={`${label}-${index}`} label={label} value={value} />)}
        </Card>
      </div>

      <nav aria-label="Continuer" className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2 md:mt-14">
        {listing ? (
          <Link to={listing.path} className="group flex min-w-0 items-center gap-4 rounded-2xl border border-line bg-white p-4 transition-[border-color,box-shadow] duration-200 hover:border-line-strong hover:shadow-soft">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-paper-dim"><FileText size={18} className="text-ink" aria-hidden /></span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm text-ink-soft">Tous les sujets</span>
              <span className="block truncate font-semibold text-ink">{context.crumbs.slice(1).map((crumb) => crumb.label).join(' · ')}</span>
            </span>
            <ArrowRight size={17} className="shrink-0 text-ink-muted transition-transform group-hover:translate-x-0.5 group-hover:text-ink" aria-hidden />
          </Link>
        ) : null}
        {context.organisme ? (
          <Link to={bulkPath(context, document)} className="group flex min-w-0 items-center gap-4 rounded-2xl border border-line bg-white p-4 transition-[border-color,box-shadow] duration-200 hover:border-line-strong hover:shadow-soft">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-paper-dim"><Files size={18} className="text-ink" aria-hidden /></span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm text-ink-soft">Plusieurs sujets d’un coup</span>
              <span className="block truncate font-semibold text-ink">Télécharger plusieurs sujets</span>
            </span>
            <ArrowRight size={17} className="shrink-0 text-ink-muted transition-transform group-hover:translate-x-0.5 group-hover:text-ink" aria-hidden />
          </Link>
        ) : null}
        <Link to="/recherche" className="group flex items-center gap-4 rounded-2xl border border-line bg-white p-4 transition-[border-color,box-shadow] duration-200 hover:border-line-strong hover:shadow-soft">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-paper-dim"><Search size={18} className="text-ink" aria-hidden /></span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm text-ink-soft">Vous cherchez un autre sujet ?</span>
            <span className="block font-semibold text-ink">Lancer une recherche</span>
          </span>
          <ArrowRight size={17} className="shrink-0 text-ink-muted transition-transform group-hover:translate-x-0.5 group-hover:text-ink" aria-hidden />
        </Link>
      </nav>

      {access.modal}
    </Container>
  );
};

export default DocumentPage;
